import dayjs from 'dayjs';
import { AdapterSource } from 'modules/types/common';
import base64 from 'react-native-base64';
import Config from 'react-native-config';
import { Dispatch } from 'redux';
import { DFDHeaders } from 'serverTypes/authentication';
import DeviceInfoServices from 'services/DeviceInfoServices';
import FileServices from 'services/FileServices';
import { currentAccountConsumerIdSelector } from 'store/account/selectors';
import { apiKeySelector } from 'store/appSettings/selectors';
import { dfdAccessTokenSelector, fhirCurrentUserPatientId } from 'store/authentication/selectors';
import { RootState } from 'store/types';
import { healthRecordsSummaryIsDownloadingSelector } from './selectors';

export const DOWNLOAD_HEALTH_RECORDS_SUMMARY =
  'HEALTH_RECORDS_SUMMARY.DOWNLOAD_HEALTH_RECORDS_SUMMARY';
export const DOWNLOAD_HEALTH_RECORDS_SUMMARY_SUCCESS =
  'HEALTH_RECORDS_SUMMARY.DOWNLOAD_HEALTH_RECORDS_SUMMARY_SUCCESS';
export const DOWNLOAD_HEALTH_RECORDS_SUMMARY_FAIL =
  'HEALTH_RECORDS_SUMMARY.DOWNLOAD_HEALTH_RECORDS_SUMMARY_FAIL';

export const downloadHealthRecordsSummary = (format: 'pdf' | 'xml') => async (
  dispatch: Dispatch,
  getState: () => RootState
) => {
  const state = getState();
  const date = dayjs(new Date()).format('MM-DD-YYYY');

  // Check for in flight downloads if there are any then return
  if (healthRecordsSummaryIsDownloadingSelector(state) === true) {
    return;
  }

  dispatch({ type: DOWNLOAD_HEALTH_RECORDS_SUMMARY });

  const patientId = fhirCurrentUserPatientId(state);
  const accessToken = dfdAccessTokenSelector(state);
  const apikey = apiKeySelector(state);
  const consumerId = currentAccountConsumerIdSelector(state);
  const deviceId = DeviceInfoServices.getDeviceId();

  let CCDUrl;
  let headers: Record<string, string | number | null> = {
    Authorization: `Bearer ${accessToken}`,
    'x-device-id': deviceId,
    'x-dfd-consumer-id': consumerId,
    'x-patient-id': patientId
  };

  if (!!Config.CCD_EXPANSION_BASE_URL) {
    CCDUrl = `${Config.CCD_EXPANSION_BASE_URL}/_download?all=true&format=${format}`;
    headers = {
      ...headers,
      'Content-Type': 'application/json',
      accept: 'application/zip',
      'mhp-audit-data': base64.encode(
        JSON.stringify({
          categoryId: '11',
          message: 'Health Record Summary'
        })
      ),
      'mhp-adapter': AdapterSource.UTAH
    };
  } else {
    CCDUrl = `${Config.CCD_BASE_URL}/download?all=true&format=${format}`;
    headers = {
      ...headers,
      'Content-Type': 'application/octet-stream',
      'x-audit-data': base64.encode(
        JSON.stringify({
          categoryId: '11',
          message: 'Health Record Summary'
        })
      )
    };
  }
  if (process.env.BUILD_ENV !== 'PROD' && CCDUrl.includes('localhost')) {
    headers[DFDHeaders.apiKeyLocalhost] = apikey;
  } else {
    headers = {
      ...headers,
      apikey
    };
  }

  try {
    await FileServices.download(`health-records-summary-${date}-${format}.zip`, CCDUrl, headers);

    dispatch({ type: DOWNLOAD_HEALTH_RECORDS_SUMMARY_SUCCESS });
  } catch (err) {
    dispatch({ type: DOWNLOAD_HEALTH_RECORDS_SUMMARY_FAIL, error: err });
  }
};

export const SHARE_HEALTH_RECORD_SUMMARY = 'MEDICAL_HISTORY.SHARE_HEALTH_RECORD_SUMMARY';
export const SHARE_HEALTH_RECORD_SUMMARY_SUCCESS =
  'MEDICAL_HISTORY.SHARE_HEALTH_RECORD_SUMMARY_SUCCESS';
export const SHARE_HEALTH_RECORD_SUMMARY_FAIL = 'MEDICAL_HISTORY.SHARE_HEALTH_RECORD_SUMMARY_FAIL';

export const shareHealthRecordSummary = (
  medHistory: any,
  toAddress: string,
  message: string,
  useSecureEmail: boolean,
  contactPhoneNumber: string,
  subject: string
) => {
  let CCDUrl;
  let headers;
  if (!!Config.CCD_EXPANSION_BASE_URL) {
    CCDUrl = '/_transmit?all=true';
    headers = {
      'mhp-audit-data': base64.encode(
        JSON.stringify({
          categoryId: '11',
          message: 'Health Record Summary'
        })
      ),
      'mhp-adapter': AdapterSource.UTAH
    };
  } else {
    CCDUrl = '/sharedocs?all=true';
    headers = {
      'x-audit-data': base64.encode(
        JSON.stringify({
          categoryId: '11',
          message: 'Health Record Summary'
        })
      )
    };
  }
  return {
    type: SHARE_HEALTH_RECORD_SUMMARY,
    payload: {
      client: 'expansionCCD',
      request: {
        method: 'post',
        url: CCDUrl,
        headers,
        data: {
          toAddress,
          message,
          useSecureEmail,
          contactPhoneNumber,
          subject
        }
      }
    }
  };
};
